import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { setAdminSession, type AdminUser } from '@/lib/session';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/login')({
  component: AdminLoginPage,
});

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

type Step =
  | { type: 'credentials' }
  | { type: 'totp-verify'; preAuthToken: string }
  | { type: 'totp-enroll'; preAuthToken: string; secret: string; otpauthUrl: string; backupCodes: string[] };

async function adminPost<T>(path: string, body: unknown, bearerToken?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (bearerToken) headers['Authorization'] = `Bearer ${bearerToken}`;
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (res.ok) {
    const json = (await res.json()) as { data: T };
    return json.data;
  }
  try {
    const err = (await res.json()) as { error: { code: string; message: string } };
    throw new ApiError(res.status, err.error.code, err.error.message);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(res.status, 'internal_error', res.statusText);
  }
}

async function adminGet<T>(path: string, bearerToken: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${bearerToken}` },
    credentials: 'include',
  });
  if (res.ok) {
    const json = (await res.json()) as { data: T };
    return json.data;
  }
  try {
    const err = (await res.json()) as { error: { code: string; message: string } };
    throw new ApiError(res.status, err.error.code, err.error.message);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(res.status, 'internal_error', res.statusText);
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'invalid_credentials') return 'Invalid credentials';
    if (err.code === 'throttled') return 'Too many attempts. Please wait and try again.';
  }
  return 'Something went wrong. Please try again.';
}

function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ type: 'credentials' });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Phase 1: credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Phase 2: TOTP code (used for both verify and enroll-confirm)
  const [totpCode, setTotpCode] = useState('');

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await adminPost<{ preAuthToken: string; totpRequired: boolean; enrollmentRequired: boolean }>(
        '/v1/admin/auth/login',
        { email, password },
      );

      if (result.enrollmentRequired) {
        const enroll = await adminGet<{ secret: string; otpauthUrl: string; backupCodes: string[] }>(
          '/v1/admin/auth/totp-enroll',
          result.preAuthToken,
        );
        setStep({ type: 'totp-enroll', preAuthToken: result.preAuthToken, ...enroll });
      } else {
        setStep({ type: 'totp-verify', preAuthToken: result.preAuthToken });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  async function handleTotpVerify(e: React.FormEvent) {
    e.preventDefault();
    if (step.type !== 'totp-verify') return;
    setError(null);
    setPending(true);
    try {
      const result = await adminPost<{ accessToken: string; adminUser: AdminUser }>(
        '/v1/admin/auth/totp',
        { code: totpCode },
        step.preAuthToken,
      );
      setAdminSession(result.accessToken, result.adminUser);
      await navigate({ to: '/admin' });
    } catch (err) {
      setError(errorMessage(err));
      setTotpCode('');
    } finally {
      setPending(false);
    }
  }

  async function handleTotpEnroll(e: React.FormEvent) {
    e.preventDefault();
    if (step.type !== 'totp-enroll') return;
    setError(null);
    setPending(true);
    try {
      const result = await adminPost<{ accessToken: string; adminUser: AdminUser }>(
        '/v1/admin/auth/totp-enroll',
        { code: totpCode, secret: step.secret, backupCodes: step.backupCodes },
        step.preAuthToken,
      );
      setAdminSession(result.accessToken, result.adminUser);
      await navigate({ to: '/admin' });
    } catch (err) {
      setError(errorMessage(err));
      setTotpCode('');
    } finally {
      setPending(false);
    }
  }

  const inputClass =
    'rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring w-full';

  if (step.type === 'credentials') {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <form onSubmit={handleCredentials} className="flex w-full max-w-sm flex-col gap-4">
          <h1 className="text-2xl font-semibold">Admin sign in</h1>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputClass}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={inputClass}
          />
          <Button type="submit" disabled={pending}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </main>
    );
  }

  if (step.type === 'totp-verify') {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <form onSubmit={handleTotpVerify} className="flex w-full max-w-sm flex-col gap-4">
          <h1 className="text-2xl font-semibold">Two-factor authentication</h1>
          <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <input
            type="text"
            inputMode="numeric"
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            autoFocus
            className={inputClass}
          />
          <Button type="submit" disabled={pending || totpCode.length !== 6}>
            {pending ? 'Verifying…' : 'Verify'}
          </Button>
        </form>
      </main>
    );
  }

  // totp-enroll
  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={handleTotpEnroll} className="flex w-full max-w-md flex-col gap-5">
        <h1 className="text-2xl font-semibold">Set up two-factor authentication</h1>
        <p className="text-sm text-muted-foreground">
          Open your authenticator app and add a new account using the link below or by
          entering the secret key manually.
        </p>

        <a
          href={step.otpauthUrl}
          className="break-all rounded-lg border border-border bg-muted px-3 py-2 text-xs font-mono hover:underline"
        >
          Open in authenticator app
        </a>

        <div className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">Or enter this secret key manually:</p>
          <code className="break-all rounded-lg border border-border bg-muted px-3 py-2 text-xs font-mono">
            {step.secret}
          </code>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">Backup codes — save these now:</p>
          <div className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted p-3">
            {step.backupCodes.map((code) => (
              <code key={code} className="text-xs font-mono">
                {code}
              </code>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Each code can only be used once.</p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm">Enter the 6-digit code from your app to confirm enrollment:</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <input
            type="text"
            inputMode="numeric"
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            autoFocus
            className={inputClass}
          />
          <Button type="submit" disabled={pending || totpCode.length !== 6}>
            {pending ? 'Confirming…' : 'Confirm and sign in'}
          </Button>
        </div>
      </form>
    </main>
  );
}
